import { Check, Circle, Plane } from 'lucide-react';

import type { CampaignLifecycleView, LifecycleStep } from '../lib/campaignLifecycle';
import { ACCENT, STATE, STROKE, SURFACE, TEXT } from '../theme/tokens';

// The campaign journey: Add devices → Map identities → Activate wave → Build
// package → Migrate → Complete.
//
// It renders `CampaignLifecycleView` and DERIVES NOTHING. The rule lives in
// ../lib/campaignLifecycle.ts, where it is asserted against ten fixtures —
// including the invariant this control depends on and cannot check for itself:
// exactly one step is `current`, every earlier one is `done`, every later one is
// `pending`. A stepper that inferred its own state from a migration row would be
// a second implementation of that rule, and the two would disagree on the day it
// mattered. `PhaseStepper` (the per-device one) is built the same way, for the
// same reason, and this deliberately mirrors its visual language so the two
// steppers in the console read as one control in two scopes.
//
// THERE IS NO SEPARATE "CAMPAIGN CHECKLIST" CARD, though mockup 1 draws one.
// This IS the checklist: same six items, same six states, same source. Two
// renderings of one state is how they drift. The REASON a step is current rides
// the top-bar CTA (and the FD-492 next-step card) rather than being restated
// here.

const ICON_SIZE = 15;

/**
 * The line under each label.
 *
 * `detail` is a fact about THIS campaign ("3 devices assigned", "2 devices
 * waiting on a mapping"); `caption` is the generic description of the step. The
 * fact wins whenever there is one — and `detail` is absent precisely when
 * nothing true can be said (the `devicePhase.ts` rule: never invent a value), so
 * the generic sentence only appears where it is the most informative thing
 * available.
 */
function line(step: LifecycleStep): string {
  return step.detail ?? step.caption;
}

export default function CampaignStepper({ view }: { view: CampaignLifecycleView }) {
  return (
    <div
      className="rounded-lg border px-5 py-4"
      style={{ borderColor: STROKE.base, backgroundColor: SURFACE.base }}
    >
      <ol
        className="m-0 flex list-none flex-col gap-4 p-0 md:flex-row md:gap-0"
        aria-label="Campaign progress"
      >
        {view.steps.map((step, index) => {
          const isLast = index === view.steps.length - 1;
          const done = step.state === 'done';
          const current = step.state === 'current';

          const circleBg = current ? ACCENT.base : done ? STATE.success.glyph : STROKE.base;
          const circleFg = step.state === 'pending' ? TEXT.secondary : '#FFFFFF';

          return (
            <li
              key={step.id}
              className="flex min-w-0 flex-1 items-start gap-3 md:flex-col md:items-center md:gap-0"
              // Colour alone is not a state a screen reader can read, and
              // `aria-current="step"` is the one attribute a stepper exists to
              // carry — the FD-467 correction to PhaseStepper, inherited here
              // rather than re-learned.
              aria-current={current ? 'step' : undefined}
            >
              <div className="flex shrink-0 items-center md:w-full">
                {/* Connectors exist only in the horizontal layout. Stacked below
                    768px they would be six dangling stubs down the left margin,
                    so they are hidden rather than redrawn vertically. */}
                <span
                  aria-hidden="true"
                  className="hidden h-[2px] flex-1 md:block"
                  style={{
                    backgroundColor:
                      index === 0
                        ? 'transparent'
                        : view.steps[index - 1].state === 'done'
                          ? STATE.success.glyph
                          : STROKE.base,
                  }}
                />
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: circleBg, color: circleFg }}
                >
                  {/* strokeWidth 2, not the console's 1.5, and deliberately: a
                      1.5 glyph inside a filled 28px disc reads as a smudge.
                      PhaseStepper made the same call for the same mark, and one
                      weight across both steppers beats a rule applied to a
                      control it was not written about. Every icon in THIS view
                      carries the same weight, which is what the rule protects. */}
                  {done ? (
                    <Check size={ICON_SIZE} strokeWidth={2} />
                  ) : current ? (
                    <Plane size={ICON_SIZE} strokeWidth={2} />
                  ) : (
                    <Circle size={7} strokeWidth={3} />
                  )}
                </span>
                <span
                  aria-hidden="true"
                  className="hidden h-[2px] flex-1 md:block"
                  style={{
                    backgroundColor: isLast
                      ? 'transparent'
                      : done
                        ? STATE.success.glyph
                        : STROKE.base,
                  }}
                />
              </div>

              <div className="min-w-0 md:mt-2 md:px-2 md:text-center">
                <div
                  className="text-[12px]"
                  style={{ color: TEXT.primary, fontWeight: current ? 600 : 400 }}
                >
                  {step.label}
                </div>
                <div className="mt-0.5 text-[10px] leading-snug" style={{ color: TEXT.secondary }}>
                  {line(step)}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
